import CloseIcon from "@mui/icons-material/CloseRounded";
import UndoIcon from "@mui/icons-material/UndoRounded";
import { Button, IconButton, InputAdornment, Tooltip } from "@mui/material";

import { ClientLink } from "@/common/components/client-link/ClientLink";
import { ReadOnlyLinkField } from "@/common/components/read-only-link-field/ReadOnlyLinkField";
import { useTranslation } from "@/translations";

interface Props {
  clientId: string;
  clientName: string;
  markedForRemoval: boolean;
  onMarkForRemoval: () => void;
  onUndoRemoval: () => void;
}

/**
 * The read-only client field of the edit dialog: the assigned client's name as a link that opens
 * the client file in a new tab (so the dialog and its unsaved edits survive), and an ✕ at the end
 * of the field that marks the client for removal. Marking strikes the name through, switches the
 * field to the warning color, and offers an undo — the assignment is only removed when the dialog
 * is saved and the removal confirmed.
 */
export function AppointmentClientField({
  clientId,
  clientName,
  markedForRemoval,
  onMarkForRemoval,
  onUndoRemoval,
}: Props) {
  const { t } = useTranslation();

  return (
    <ReadOnlyLinkField
      label={t("common.client")}
      value={clientName}
      link={
        <Tooltip title={t("calendar.appointmentClient.openClientFileInNewTab")}>
          <span>
            <ClientLink
              clientId={clientId}
              name={clientName}
              variant="body2"
              strikeThrough={markedForRemoval}
              openInNewTab
            />
          </span>
        </Tooltip>
      }
      warning={markedForRemoval}
      helperText={markedForRemoval ? t("calendar.appointmentClient.removalHint") : undefined}
      endAdornment={
        <InputAdornment position="end">
          {markedForRemoval ? (
            <Button size="small" color="warning" startIcon={<UndoIcon />} onClick={onUndoRemoval}>
              {t("calendar.appointmentClient.undoRemoval")}
            </Button>
          ) : (
            <Tooltip title={t("calendar.appointmentClient.markForRemoval")}>
              <IconButton
                size="small"
                onClick={onMarkForRemoval}
                aria-label={t("calendar.appointmentClient.markForRemoval")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </InputAdornment>
      }
    />
  );
}
